import { useState, useCallback } from "react";

// Custom hook for article pagination logic
const useArticlePagination = (articles) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Calculate articles per page based on screen size
  const getArticlesPerPage = useCallback(() => {
    return window.innerWidth < 768 ? 1 : 3;
  }, []);

  // Get visible articles for current page
  const getVisibleArticles = useCallback(() => {
    const articlesPerPage = getArticlesPerPage();
    const startIndex = currentIndex;
    const endIndex = Math.min(startIndex + articlesPerPage, articles.length);
    return articles.slice(startIndex, endIndex);
  }, [articles, currentIndex, getArticlesPerPage]);

  // Navigation handlers
  const handlePrevious = useCallback(() => {
    const articlesPerPage = getArticlesPerPage();
    setCurrentIndex(Math.max(0, currentIndex - articlesPerPage));
  }, [currentIndex, getArticlesPerPage]);

  const handleNext = useCallback(() => {
    const articlesPerPage = getArticlesPerPage();
    const maxIndex = Math.max(0, articles.length - articlesPerPage);
    setCurrentIndex(Math.min(maxIndex, currentIndex + articlesPerPage));
  }, [articles.length, currentIndex, getArticlesPerPage]);

  // Navigation state
  const canGoPrevious = currentIndex > 0;
  const canGoNext = useCallback(() => {
    const articlesPerPage = getArticlesPerPage();
    return currentIndex + articlesPerPage < articles.length;
  }, [articles.length, currentIndex, getArticlesPerPage]);

  return {
    currentIndex,
    setCurrentIndex,
    getVisibleArticles,
    handlePrevious,
    handleNext,
    canGoPrevious,
    canGoNext: canGoNext(),
    articlesPerPage: getArticlesPerPage(),
    totalPages: Math.ceil(articles.length / getArticlesPerPage()),
  };
};

export default useArticlePagination;
